import type { DimensionStatus, DimensionTone, GapAnalysisDimension } from "../components/gapAnalysisConstants";

export type FindingPriority = "HIGH" | "MEDIUM" | "LOW";

/** Grouped findings as stored in Neo4j (v3 format). */
export interface GroupedFindings {
  high: string[];
  medium: string[];
  low: string[];
}

/**
 * Parse the `detailedFindings` JSON string into grouped findings.
 * Handles both new v3 format ({high, medium, low}) and returns empty groups on failure.
 */
export function parseGroupedFindings(detailedFindings: string | null | undefined): GroupedFindings {
  const empty: GroupedFindings = { high: [], medium: [], low: [] };

  if (!detailedFindings) {
    return empty;
  }

  try {
    const parsed = JSON.parse(detailedFindings) as unknown;

    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return empty;
    }

    const obj = parsed as Record<string, unknown>;
    const toStringArray = (val: unknown): string[] => {
      if (!Array.isArray(val)) return [];
      return val.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
    };

    return {
      high: toStringArray(obj.high),
      medium: toStringArray(obj.medium),
      low: toStringArray(obj.low),
    };
  } catch {
    return empty;
  }
}

export function getOverallStatusLabel(dimensions: GapAnalysisDimension[]): {
  status: DimensionStatus;
  tone: DimensionTone;
} {
  const hasCritical = dimensions.some((d) => d.status === "Critical");
  const hasNeedsWork = dimensions.some((d) => d.status === "Needs work");

  // Never show an overall "Critical" judgment.
  if (hasCritical || hasNeedsWork) {
    return { status: "Needs work", tone: "warning" };
  }

  const allStrong = dimensions.every((d) => d.status === "Strong");

  if (allStrong) {
    return { status: "Strong", tone: "success" };
  }

  return { status: "Good", tone: "brand" };
}

export function groupDimensionsByStatus(
  dimensions: GapAnalysisDimension[]
): Array<{ status: DimensionStatus; tone: DimensionTone; dimensions: GapAnalysisDimension[] }> {
  const statusOrder: DimensionStatus[] = ["Critical", "Needs work", "Good", "Strong"];
  const toneMap: Record<DimensionStatus, DimensionTone> = {
    Critical: "error",
    "Needs work": "warning",
    Good: "brand",
    Strong: "success",
  };

  return statusOrder
    .map((status) => ({
      status,
      tone: toneMap[status],
      dimensions: dimensions.filter((d) => d.status === status),
    }))
    .filter((group) => group.dimensions.length > 0);
}
